class VisualNavigation extends HTMLElement {
  connectedCallback() {
    if (this.isConnectedToMenu) return;

    this.isConnectedToMenu = true;
    this.triggers = Array.from(this.querySelectorAll("[data-visual-nav-trigger]"));
    this.panels = Array.from(this.querySelectorAll("[data-visual-nav-panel]"));
    this.topLinks = Array.from(this.querySelectorAll(".visual-nav__top-link"));
    this.triggerHandlers = new Map();
    this.activeTrigger = null;

    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleFocusout = this.handleFocusout.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleScroll = this.handleScroll.bind(this);

    this.triggers.forEach((trigger) => {
      const handler = () => this.toggle(trigger);
      this.triggerHandlers.set(trigger, handler);
      trigger.addEventListener("click", handler);
    });

    this.addEventListener("focusout", this.handleFocusout);
    document.addEventListener("click", this.handleDocumentClick);
    document.addEventListener("keydown", this.handleKeydown);
    window.addEventListener("resize", this.handleResize);
    window.addEventListener("scroll", this.handleScroll, { passive: true });
  }

  disconnectedCallback() {
    this.triggerHandlers?.forEach((handler, trigger) => {
      trigger.removeEventListener("click", handler);
    });
    this.removeEventListener("focusout", this.handleFocusout);
    document.removeEventListener("click", this.handleDocumentClick);
    document.removeEventListener("keydown", this.handleKeydown);
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("scroll", this.handleScroll);
    this.isConnectedToMenu = false;
  }

  toggle(trigger) {
    const shouldOpen = trigger.getAttribute("aria-expanded") !== "true";

    this.close();
    if (shouldOpen) this.open(trigger);
  }

  open(trigger) {
    const panelId = trigger.dataset.visualNavTrigger;
    const panel = this.querySelector(`#${CSS.escape(panelId)}`);
    if (!panel) return;

    trigger.setAttribute("aria-expanded", "true");
    panel.hidden = false;
    this.activeTrigger = trigger;
  }

  close({ restoreFocus = false } = {}) {
    const triggerToFocus = this.activeTrigger;

    this.triggers.forEach((trigger) => trigger.setAttribute("aria-expanded", "false"));
    this.panels.forEach((panel) => {
      panel.hidden = true;
    });
    this.activeTrigger = null;

    if (restoreFocus && triggerToFocus) triggerToFocus.focus();
  }

  handleDocumentClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  handleFocusout() {
    window.setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    }, 0);
  }

  handleKeydown(event) {
    if (event.key === "Escape" && this.activeTrigger) {
      event.preventDefault();
      this.close({ restoreFocus: true });
      return;
    }

    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

    const currentIndex = this.topLinks.indexOf(document.activeElement);
    if (currentIndex === -1) return;

    event.preventDefault();
    const direction = event.key === "ArrowRight" ? 1 : -1;
    const nextIndex = (currentIndex + direction + this.topLinks.length) % this.topLinks.length;
    this.topLinks[nextIndex].focus();
  }

  handleResize() {
    if (window.innerWidth < 769) this.close();
  }

  handleScroll() {
    if (this.activeTrigger) this.close();
  }
}

if (!customElements.get("visual-navigation")) {
  customElements.define("visual-navigation", VisualNavigation);
}
